/**
 * Small helpers for writing the accompaniment of a track: repeating a line, zither arpeggios over a
 * root per bar, held drones. They only build jianpu strings (notation.ts).
 */
import { pentaIndex, pentaToken } from './theory';

/** A line `n` times over. */
export const rep = (line: string, n: number): string => Array.from({ length: n }, () => line).join(' | ');

/**
 * Arpeggios over one root a bar ("6, 3, 5, 6,"): each pattern entry is scale steps above the root
 * (null: a rest), every one `unit` long (`_` an eighth, `` a beat, `__` a sixteenth).
 */
export function arp(roots: string, pattern: readonly (number | null)[], unit = '_'): string {
  return roots
    .trim()
    .split(/\s+/)
    .map((r) => {
      const i = pentaIndex(r);
      return pattern.map((p) => (p === null ? `0${unit}` : `${pentaToken(i + p)}${unit}`)).join(' ');
    })
    .join(' | ');
}

/** A held drone a bar long on each root, with the fifth above (three scale steps): "1,&5,---". */
export function drone(roots: string, beats: number, fifth = true): string {
  return roots
    .trim()
    .split(/\s+/)
    .map((r) => {
      const i = pentaIndex(r);
      return `${r}${fifth ? `&${pentaToken(i + 3)}` : ''}${'-'.repeat(beats - 1)}`;
    })
    .join(' | ');
}

/** The bars of a line (split at `|`). */
export const bars = (line: string): string[] => line.split('|').map((b) => b.trim());

/** A line with bars from `at` on replaced (a variation's new ending). */
export function vary(line: string, at: number, ...repl: string[]): string {
  const b = bars(line);
  b.splice(at, repl.length, ...repl);
  return b.join(' | ');
}

/** A line moved `n` octaves (each degree gains `'`, or loses a `,`). */
export function octave(line: string, n: number): string {
  return line.replace(/([#b]?[1-7])([',]*)/g, (_, d: string, marks: string) => {
    let o = 0;
    for (const c of marks) o += c === "'" ? 1 : -1;
    o += n;
    return d + (o > 0 ? "'" : ',').repeat(Math.abs(o));
  });
}

/** Rests filling `beats` whole beats. */
export const rest = (beats: number): string => `0${'-'.repeat(Math.max(0, beats - 1))}`;
